/**
 * Verify — Public provably-fair verifier. Renders standalone with brand chrome
 * (no sidebar; anyone can open the page without an account).
 */
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShieldCheck, ChevronDown, ChevronRight } from "lucide-react";

import api from "../lib/api";
import { extractError } from "../lib/formatError";
import Input from "../components/ui-r1/Input";
import Button from "../components/ui-r1/Button";
import { Card, CardBody, CardHeader, CardLabel, CardTitle } from "../components/ui-r1/Card";
import { Badge } from "../components/ui-r1/Badge";

const COLOR_STYLE = {
  red: { text: "#FF6B6B", ring: "rgba(255,107,107,0.4)" },
  black: { text: "#F4EDE0", ring: "rgba(244,237,224,0.3)" },
  gold: { text: "#FFCE55", ring: "rgba(255,206,85,0.4)" },
  tattoo: { text: "#FF3B7A", ring: "rgba(255,59,122,0.4)" },
  no_tattoo: { text: "#F4EDE0", ring: "rgba(244,237,224,0.3)" },
};

// Display labels for text-style choices — mirrors backend games.choice_labels
// for slugs that surface non-color choice values.
const CHOICE_LABELS = {
  "sexy-tattoo": { tattoo: "TATTOO", no_tattoo: "NO TATTOO" },
};

function formatChoice(gameSlug, choiceValue) {
  return CHOICE_LABELS[gameSlug]?.[choiceValue] || (choiceValue || "").toUpperCase();
}

export default function Verify() {
  const [params] = useSearchParams();
  const [serverSeed, setServerSeed] = useState(params.get("server_seed") || "");
  const [clientSeed, setClientSeed] = useState(params.get("client_seed") || "");
  const [nonce, setNonce] = useState(params.get("nonce") || "0");
  const [gameSlug, setGameSlug] = useState(params.get("game_slug") || "bra-3-way");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showHow, setShowHow] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setError(null); setResult(null); setBusy(true);
    try {
      const { data } = await api.get(`/games/${gameSlug}/verify`, {
        params: { server_seed: serverSeed, client_seed: clientSeed, nonce: Number(nonce) },
      });
      setResult(data);
    } catch (e2) {
      setError(extractError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="verify-page">
      {/* Brand top bar */}
      <header className="border-b border-line bg-elevated/85 backdrop-blur">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="verify-logo">
            <span className="h-8 w-8 rounded-lg bg-brand-gradient flex items-center justify-center font-display italic text-white shadow-glow-pink">B</span>
            <span className="font-display italic text-lg text-ink">Bra Game</span>
          </Link>
          <Badge variant="gold" className="hidden sm:inline-flex">
            <ShieldCheck className="w-3 h-3 mr-1" /> Public verifier
          </Badge>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-[10px] tracking-[0.4em] uppercase text-pink mb-3">Provably fair</div>
        <h1 className="font-display italic text-4xl sm:text-5xl text-ink leading-tight">Verify a round.</h1>
        <p className="text-sm text-ink-2 mt-4 max-w-xl">
          Paste your server seed (revealed after the round), client seed, and nonce. We re-run the HMAC and show every
          intermediate value so you can confirm the outcome could not have been tampered with.
        </p>

        <Card className="mt-8">
          <CardBody>
            <form onSubmit={submit} className="space-y-4" data-testid="verify-form">
              <div>
                <label className="text-[10px] tracking-[0.3em] uppercase text-ink-3 mb-1.5 block">Game</label>
                <select
                  value={gameSlug}
                  onChange={(e) => setGameSlug(e.target.value)}
                  className="w-full h-11 rounded-lg border border-line bg-base text-ink px-3 text-sm focus:border-pink focus:outline-none focus:shadow-glow-pink transition"
                  data-testid="verify-game-slug"
                >
                  <option value="bra-3-way">Bra 3-Way (mod 3 · red/black/gold)</option>
                  <option value="bra-2-way">Bra 2-Way (mod 2 · red/black)</option>
                  <option value="sexy-tattoo">Sexy Tattoo (mod 2 · tattoo/no_tattoo)</option>
                  <option value="hot-bikini">Hot Bikini (mod 2 · red/black)</option>
                  <option value="scratch-card">Scratch Card (weighted prize table · card price → payout)</option>
                </select>
              </div>
              <Input label="Server seed (revealed)" value={serverSeed} onChange={(e) => setServerSeed(e.target.value)} data-testid="verify-server-seed" inputClassName="font-mono text-xs" required />
              <Input label="Client seed" value={clientSeed} onChange={(e) => setClientSeed(e.target.value)} data-testid="verify-client-seed" inputClassName="font-mono text-xs" required />
              <Input label="Nonce" type="number" value={nonce} onChange={(e) => setNonce(e.target.value)} data-testid="verify-nonce" inputClassName="font-mono text-xs" required />

              {error && (
                <div className="text-xs text-danger border border-danger/60 bg-danger/10 rounded-lg px-3 py-2" data-testid="verify-error">
                  {error}
                </div>
              )}
              <Button type="submit" variant="primary" size="lg" loading={busy} className="w-full sm:w-auto" data-testid="verify-submit-btn">
                {busy ? "Computing…" : "Verify round"}
              </Button>
            </form>
          </CardBody>
        </Card>

        {result && (result.game_slug === "scratch-card" || gameSlug === "scratch-card") ? (
          <Card variant="elevated" className="mt-6" data-testid="verify-result">
            <CardHeader>
              <CardLabel>Computation</CardLabel>
              <CardTitle>Scratch prize outcome</CardTitle>
            </CardHeader>
            <CardBody className="space-y-2">
              <Row label="Game" value={result.game_slug || gameSlug} mono />
              <Row label="Roll (0-9999)" value={String(result.roll ?? "—")} mono />
              <Row label="Weight index" value={String(result.weight_index ?? "—")} mono />
              <div className="pt-4 mt-2 border-t border-line flex items-center justify-between">
                <div className="text-[10px] tracking-[0.3em] uppercase text-ink-2">Prize multiplier</div>
                <div
                  className="font-display italic text-4xl px-4"
                  style={{
                    color: Number(result.prize_multiplier) > 0 ? "#FFCE55" : "#F4EDE0",
                    textShadow: Number(result.prize_multiplier) > 0
                      ? "0 0 30px rgba(255,206,85,0.4)"
                      : "0 0 30px rgba(244,237,224,0.2)",
                  }}
                  data-testid="verify-prize-multiplier"
                >
                  {Number(result.prize_multiplier || 0)}×
                </div>
              </div>
              <p className="text-[11px] text-ink-3 leading-relaxed pt-3">
                Scratch cards use HMAC(server_seed, &quot;{result.client_seed}:{result.nonce}&quot;) →
                32-bit int → mod 10 000 = roll. The roll walks the weighted prize table until it
                lands inside a bucket — that&apos;s your multiplier. A 0× result means the card was
                a loser; positive values are winners.
              </p>
            </CardBody>
          </Card>
        ) : result && (
          <Card variant="elevated" className="mt-6" data-testid="verify-result">
            <CardHeader>
              <CardLabel>Computation</CardLabel>
              <CardTitle>Round outcome</CardTitle>
            </CardHeader>
            <CardBody className="space-y-2">
              <Row label="Message" value={result.message} mono />
              <Row label="HMAC-SHA256 (hex)" value={result.hmac_hex} mono breakAll />
              <Row label="First 8 hex chars" value={result.first_hex} mono />
              <Row label="Integer value" value={String(result.int_value)} mono />
              <Row label={`mod ${result.n_colors || 3}`} value={String(result.mod_n ?? result.mod3)} mono />
              <Row label="Game" value={result.game_slug || gameSlug} mono />
              <div className="pt-4 mt-2 border-t border-line flex items-center justify-between">
                <div className="text-[10px] tracking-[0.3em] uppercase text-ink-2">Result</div>
                <div
                  className="font-display italic text-4xl uppercase px-4"
                  style={{
                    color: COLOR_STYLE[result.color]?.text,
                    textShadow: `0 0 30px ${COLOR_STYLE[result.color]?.ring}`,
                  }}
                  data-testid="verify-color"
                >
                  {formatChoice(result.game_slug || gameSlug, result.color)}
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        <div className="mt-10">
          <button
            onClick={() => setShowHow((s) => !s)}
            className="flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-ink-2 hover:text-pink transition"
            data-testid="how-toggle-btn"
          >
            {showHow ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            How does provably fair work?
          </button>
          {showHow && (
            <Card className="mt-4" data-testid="how-content">
              <CardBody className="text-sm text-ink-2 leading-relaxed space-y-3">
                <p>
                  Every round is decided by a cryptographic hash the server commits to <em>before</em> you place the bet.
                  You see the SHA-256 hash of the server seed, but not the seed itself. After the reveal, the server
                  hands you the plain seed so you can verify two things:
                </p>
                <ol className="list-decimal pl-6 space-y-1">
                  <li>The seed hashes to the same commit you saw earlier — proves the server didn't change the seed mid-round.</li>
                  <li>Re-running the HMAC below with your client seed and nonce gives the same color the server showed — proves the outcome was determined by the pre-committed seed.</li>
                </ol>
                <pre className="text-[10px] font-mono bg-base border border-line rounded-lg p-4 overflow-x-auto whitespace-pre-wrap break-all text-ink">
{`server_seed_hash  = sha256(server_seed_hex)
message           = f"{client_seed}:{nonce}"
hmac_hex          = HMAC-SHA256(key=server_seed_hex.encode(), msg=message).hexdigest()
first_hex         = hmac_hex[:8]
int_value         = int(first_hex, 16)
# N depends on the game — 3 for Bra 3-Way, 2 for Bra 2-Way
mod_n             = int_value % N
color             = colors[mod_n]        # ["red","black","gold"] or ["red","black"]`}
                </pre>
                <p>
                  Nonce increments after each resolved round and resets to 0 whenever you rotate seeds. You can rotate
                  at any time — but only between rounds.
                </p>
              </CardBody>
            </Card>
          )}
        </div>
      </main>

      <footer className="border-t border-line mt-16 py-6 px-6">
        <div className="max-w-5xl mx-auto text-[10px] tracking-widest uppercase text-ink-3 text-center">
          © 2026 Bra Game N.V. · Anjouan License #ALSI-XXXXXX-XX
        </div>
      </footer>
    </div>
  );
}

function Row({ label, value, mono, breakAll }) {
  return (
    <div className="flex justify-between items-start gap-3 text-xs">
      <span className="text-[10px] tracking-[0.25em] uppercase text-ink-2 pt-0.5 shrink-0">{label}</span>
      <span className={`${mono ? "font-mono" : ""} ${breakAll ? "break-all" : ""} text-ink text-right`}>{value}</span>
    </div>
  );
}
